/**
 * ErrorBoundary — a render exception anywhere in the tree used to unmount
 * the whole app to a white screen inside the Shopify admin. This catches
 * it, reports to Sentry when it's loaded, and shows a recoverable card.
 *
 * `resetKey` (the current pathname in AppLayout) clears the error when the
 * merchant navigates elsewhere, so one broken page doesn't brick the rest.
 */
import React from 'react';
import { Button, Card, BlockStack, Text } from '@shopify/polaris';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    try {
      // Sentry is loaded lazily in index.js only when configured.
      import('@sentry/react').then((Sentry) => {
        if (Sentry?.getClient?.()) Sentry.captureException(error, { extra: { componentStack: info?.componentStack } });
      }).catch(() => {});
    } catch { /* never let reporting throw */ }
    // eslint-disable-next-line no-console
    console.error('Render error:', error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div style={{ maxWidth: 560, margin: '48px auto', padding: '0 16px' }}>
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">Something went wrong</Text>
            <Text as="p" tone="subdued">
              This screen hit an unexpected error. Your data is safe — reload to continue, and if it keeps
              happening, message support from the Help button.
            </Text>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="primary" onClick={() => window.location.reload()}>Reload</Button>
              <Button onClick={() => this.setState({ error: null })}>Try again</Button>
            </div>
          </BlockStack>
        </Card>
      </div>
    );
  }
}
